// Редовете на доставката като карти (под десктоп ширина): име · ⚑ · ✕, после
// полета с етикет — Разфасовка, Количество, Ед. цена, Партида, Срок, Склад — и
// ред „Нето · ДДС · Бруто". Полетата носят СЪЩИТЕ id-та (`dwx-{продукт}-{кол}`)
// като клетките на решетката, затова фокусът след добавяне работи еднакво;
// Нето/Бруто тук са само за четене (липсващите клетки стрелките прескачат).
import { DatePicker, Select } from "antd";
import { CloseOutlined, FlagOutlined, SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import type { Packaging, Product, StockBalance, Warehouse } from "../../../../api/inventory";
import { PickCell } from "../../components/PickCell";
import { NegativeStockMarker } from "../../components/NegativeStockMarker";
import {
  FLAT_CELL_ERROR_CLASS,
  formatQtyInput,
  type DeliveryRowDraft,
} from "./deliveryWorksheetShared";
import type { NumericCol } from "./DeliveryWorksheetTable";
import { formatSignedDelta } from "./deliveryTotalAdjust";
import type { DeliveryIssueField } from "./deliveryIssues";

type Props = {
  addedProducts: Product[];
  rowOf: (productId: number) => DeliveryRowDraft;
  balanceByProduct: Map<number, StockBalance>;
  packagingById: Map<number, Packaging>;
  warehouses: Warehouse[];
  decimalsOf: (productId: number) => number;
  cellId: (productId: number, col: number) => string;
  vatApplies: boolean;
  onCellChange: (productId: number, col: NumericCol | "batch", value: string) => void;
  onCellBlur: (productId: number, col: NumericCol) => void;
  onPackagingChange: (productId: number, packagingId: number | null) => void;
  onExpiryChange: (productId: number, expiry: string) => void;
  onRowWarehouseChange: (productId: number, warehouseId: number) => void;
  onRemoveRow: (productId: number) => void;
  onFlag: (productId: number) => void;
  // Червената рамка — същото правило като в решетката
  isCellInvalid: (productId: number, field: DeliveryIssueField) => boolean;
};

// Полетата на картата — с рамка (докосване с пръст), 16px срещу зуума на iOS
const FIELD_CLASS =
  "box-border w-full rounded-lg border border-shade-3 bg-surface px-2.5 py-2.5 text-base leading-[normal] text-ink outline-none focus:border-brand focus:shadow-[0_0_0_3px_var(--color-brand-tint)]";

export const DeliveryLineCards = ({
  addedProducts,
  rowOf,
  balanceByProduct,
  packagingById,
  warehouses,
  decimalsOf,
  cellId,
  vatApplies,
  onCellChange,
  onCellBlur,
  onPackagingChange,
  onExpiryChange,
  onRowWarehouseChange,
  onRemoveRow,
  onFlag,
  isCellInvalid,
}: Props) => {
  const { t } = useTranslation();
  if (!addedProducts.length)
    return (
      <div className="flex flex-col items-center gap-2.5 px-6 py-12 text-center">
        <div className="flex h-[46px] w-[46px] items-center justify-center rounded-[13px] bg-brand-tint">
          <SearchOutlined className="text-xl text-brand-deep-ink" />
        </div>
        <div className="max-w-[420px] text-[13px] leading-[1.6] text-muted">{t("inventoryPage.wsEmptyHint")}</div>
      </div>
    );

  const label = (text: string) => (
    <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted">{text}</span>
  );

  return (
    <div>
      {addedProducts.map((product, index) => {
        const row = rowOf(product.id);
        const balance = balanceByProduct.get(product.id);
        const decimals = decimalsOf(product.id);
        const expiryError = isCellInvalid(product.id, "expiry");
        const batchError = isCellInvalid(product.id, "batch");
        const packagingOptions = [
          { value: 0, label: product.unit ?? "—" },
          ...(product.packagingIds ?? [])
            .map((id) => packagingById.get(id))
            .filter((pkg): pkg is Packaging => !!pkg)
            .map((pkg) => ({ value: pkg.id, label: pkg.name })),
        ];
        const numberField = (col: NumericCol, colIndex: number, value: string, invalid = false) => (
          <input
            id={cellId(product.id, colIndex)}
            data-rk={product.id}
            data-c={colIndex}
            inputMode="decimal"
            enterKeyHint="next"
            value={value}
            placeholder={col === "qty" ? "0" : "0.00"}
            onChange={(e) => onCellChange(product.id, col, e.target.value)}
            onFocus={(e) => e.target.select()}
            onBlur={() => onCellBlur(product.id, col)}
            // изведената от въведена сума цена — сива, с подсказка (като на решетката)
            title={col === "price" && row.typedSum ? t("inventoryPage.upPriceFromSum") : undefined}
            aria-invalid={invalid || undefined}
            className={`${FIELD_CLASS} text-right font-sheet ${
              col === "price" && row.priceAuto
                ? "italic text-muted"
                : col === "price" && row.typedSum
                  ? "text-muted"
                  : "font-semibold"
            } ${invalid ? FLAT_CELL_ERROR_CLASS : ""}`}
          />
        );
        const pickClass = (muted: boolean, error = false) =>
          `${FIELD_CLASS} cursor-pointer truncate ${muted ? "text-ghost" : ""} ${error ? FLAT_CELL_ERROR_CLASS : ""}`;
        return (
          <div
            key={product.id}
            className={`flex flex-col gap-2.5 border-t border-shade-1 px-3.5 py-3 ${
              row.problem ? "bg-danger-tint/40" : ""
            }`}
          >
            <div className="flex items-start gap-2">
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="font-semibold text-ink">
                  {index + 1}. {product.name}
                </span>
                <span className="text-xs text-muted">
                  {t("inventoryPage.productOptStock")}:{" "}
                  {formatQtyInput(Math.max(0, balance?.quantity ?? 0), decimals)}
                  {product.unit ? ` ${product.unit}` : ""}
                  {/* минусът от продажби — до клампнатата нула */}
                  <NegativeStockMarker
                    quantity={balance?.quantity ?? 0}
                    decimals={decimals}
                    className="ml-1 font-semibold"
                  />
                </span>
                {row.problem && (
                  <span className="self-start rounded-md bg-danger-tint px-2 py-0.5 text-xs text-danger-ink">
                    ⚑ {row.problem}
                  </span>
                )}
                {!!row.totalAdjust && (
                  <span className="text-[11.5px] text-brand-deep-ink">
                    {t("inventoryPage.tadjRowNote", { delta: formatSignedDelta(row.totalAdjust) })}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => onFlag(product.id)}
                aria-label={t("inventoryPage.problemFlagHint")}
                className={`flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-[7px] border border-shade-3 bg-surface ${
                  row.problem ? "text-danger-ink" : "text-body"
                }`}
              >
                <FlagOutlined />
              </button>
              <button
                type="button"
                onClick={() => onRemoveRow(product.id)}
                aria-label={t("inventoryPage.wsClearRowHint")}
                className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-[7px] border border-shade-3 bg-surface text-body"
              >
                <CloseOutlined />
              </button>
            </div>
            <div className="grid grid-cols-[repeat(auto-fit,minmax(120px,1fr))] gap-2">
              {packagingOptions.length > 1 && (
                <label className="flex flex-col gap-1">
                  {label(t("inventoryPage.colPackaging"))}
                  <PickCell
                    id={cellId(product.id, 0)}
                    rowKey={product.id}
                    col={0}
                    className={pickClass(false)}
                    renderEditor={(close) => (
                      <Select
                        autoFocus
                        defaultOpen
                        value={row.packagingId ?? 0}
                        onChange={(v) => {
                          onPackagingChange(product.id, v === 0 ? null : (v as number));
                          close();
                        }}
                        onBlur={() => close(false)}
                        options={packagingOptions}
                        className="w-full"
                      />
                    )}
                  >
                    {row.packagingId
                      ? (packagingById.get(row.packagingId)?.name ?? "—")
                      : (product.unit ?? "—")}
                  </PickCell>
                </label>
              )}
              <label className="flex flex-col gap-1">
                {label(`${t("inventoryPage.colQty")}${product.unit ? ` · ${product.unit}` : ""}`)}
                {numberField("qty", 1, row.qty, isCellInvalid(product.id, "qty"))}
              </label>
              <label className="flex flex-col gap-1">
                {label(t("inventoryPage.colUnitPrice"))}
                {numberField("price", 2, row.price, isCellInvalid(product.id, "price"))}
              </label>
              {product.trackBatch && (
                <label className="flex flex-col gap-1">
                  {label(t("inventoryPage.colBatch"))}
                  <input
                    id={cellId(product.id, 5)}
                    data-rk={product.id}
                    data-c={5}
                    value={row.batch}
                    onChange={(e) => onCellChange(product.id, "batch", e.target.value)}
                    className={`${FIELD_CLASS} ${batchError ? FLAT_CELL_ERROR_CLASS : ""}`}
                  />
                </label>
              )}
              {product.trackExpiry && (
                <label className="flex flex-col gap-1">
                  {label(t("inventoryPage.colExpiry"))}
                  <PickCell
                    id={cellId(product.id, 6)}
                    rowKey={product.id}
                    col={6}
                    className={pickClass(!row.expiry, expiryError)}
                    renderEditor={(close) => (
                      <DatePicker
                        autoFocus
                        defaultOpen
                        inputReadOnly
                        value={row.expiry ? dayjs(row.expiry) : null}
                        format="DD.MM.YYYY"
                        onChange={(d) => {
                          onExpiryChange(product.id, d ? d.format("YYYY-MM-DD") : "");
                          close();
                        }}
                        onOpenChange={(open) => {
                          if (!open) close();
                        }}
                        className="w-full"
                      />
                    )}
                  >
                    {row.expiry ? dayjs(row.expiry).format("DD.MM.YYYY") : "—"}
                  </PickCell>
                </label>
              )}
              {warehouses.length > 1 && (
                <label className="flex flex-col gap-1">
                  {label(t("inventoryPage.colWarehouse"))}
                  <PickCell
                    id={cellId(product.id, 7)}
                    rowKey={product.id}
                    col={7}
                    className={pickClass(false, isCellInvalid(product.id, "warehouse"))}
                    renderEditor={(close) => (
                      <Select
                        autoFocus
                        defaultOpen
                        value={row.warehouseId ?? undefined}
                        onChange={(v) => {
                          onRowWarehouseChange(product.id, v as number);
                          close();
                        }}
                        onBlur={() => close(false)}
                        options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
                        className="w-full"
                      />
                    )}
                  >
                    {warehouses.find((w) => w.id === row.warehouseId)?.name ?? "—"}
                  </PickCell>
                </label>
              )}
            </div>
            <div className="flex flex-wrap justify-end gap-x-2.5 gap-y-1 text-[12.5px] text-body">
              <span>
                {t("inventoryPage.colNet")} <span className="font-sheet text-ink">{row.net || "—"}</span>
              </span>
              <span>
                · {t("inventoryPage.colVat")} {vatApplies && product.vatRate != null ? `${product.vatRate}%` : "—"}
              </span>
              <span>
                · {t("inventoryPage.colGross")}{" "}
                <span className="font-sheet font-semibold text-ink">{row.gross || "—"}</span>
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default DeliveryLineCards;
